import { formatNumber } from '@angular/common';

import {
    IStairCalculationResult,
    IStairConfig,
    IValidationItem,
    IWoodMaterial,
    SHOW_MATERIAL_COST,
    WOOD_MATERIALS,
} from '@stair-craft/common/util';

import { EHeroVariant, HERO_VARIANTS } from './home-hero.logic';

export interface IHeroCardRow {
    readonly label: string;
    readonly value: string;
    /** The value passed its check: shown in the success colour */
    readonly ok: boolean;
}

export interface IHeroCardCta {
    readonly label: string;
    readonly link: string;
}

/** A card over the video, shown over `[from, to]` of the progress */
export interface IHeroCard {
    /** "01 · Room": the step number and the step word */
    readonly kicker: string;
    readonly title: string;
    /** Serif tail of the title; empty when the title has none */
    readonly titleEm: string;
    readonly text: string;
    readonly rows: readonly IHeroCardRow[];
    readonly cta: IHeroCardCta | null;
    readonly from: number;
    readonly to: number;
}

/** A configuration and what the calculator gave for it: the card numbers are the calculator's own */
export interface IHeroCalculation {
    readonly config: IStairConfig;
    readonly result: IStairCalculationResult;
}

/** A card before its stage window is known */
type IHeroCardDraft = Omit<IHeroCard, 'from' | 'to'>;

function row(label: string, value: string, ok: boolean = false): IHeroCardRow {
    return { label, value, ok };
}

/**
 * The check of one norm passed outright: its item in the calculator's norm check is `ok`. A
 * warning is not a pass — the card shows the same verdict as the constructor's summary.
 */
function passes(result: IStairCalculationResult, code: string): boolean {
    return result.validation.some((item: IValidationItem) => item.code === code && item.severity === 'ok');
}

/** A row of a norm check: the value with its pass or fail mark */
function checkRow(label: string, value: string, ok: boolean): IHeroCardRow {
    return row(label, `${value} ${ok ? '✓' : '!'}`, ok);
}

function card(step: string, word: string, title: string, text: string, rows: readonly IHeroCardRow[]): IHeroCardDraft {
    return { title, text, rows, kicker: `${step} · ${word}`, titleEm: '', cta: null };
}

/** The cost row, or none: the cost is hidden with every other cost on the site while SHOW_MATERIAL_COST is off */
function costRows(value: string): IHeroCardRow[] {
    return SHOW_MATERIAL_COST ? [row($localize`:@@homeMiniCostUsd:Стоимость`, value)] : [];
}

function woodName(id: string): string {
    return WOOD_MATERIALS.find((material: IWoodMaterial) => material.id === id)?.name ?? id;
}

function heroCardDrafts(variant: EHeroVariant, { config, result }: IHeroCalculation, locale: string): IHeroCardDraft[] {
    const unitMm: string = $localize`:@@unitMm:мм`;
    const unitDeg: string = $localize`:@@unitDeg:°`;
    const unitM3: string = $localize`:@@unitM3:м³`;
    const unitKg: string = $localize`:@@unitKg:кг`;
    const number: (value: number, digits: string) => string = (value: number, digits: string): string =>
        formatNumber(value, locale, digits);
    const mm: (value: number) => string = (value: number): string => `${number(value, '1.0-1')} ${unitMm}`;

    const roomSize: IHeroCardRow[] = [
        row($localize`:@@homeMiniRoomLength:Длина`, mm(config.roomLength)),
        row($localize`:@@homeMiniRoomWidth:Ширина`, mm(config.roomWidth)),
        row($localize`:@@homeMiniRoomHeight:Высота`, mm(config.floorHeight)),
    ];
    const slab: IHeroCardRow = row($localize`:@@homeMiniRoomFloor:Перекрытие`, mm(config.floorThickness));
    const opening: IHeroCardRow = row(
        $localize`:@@homeMiniRoomOpening:Проём`,
        `${number(config.openingLength, '1.0-0')}×${number(config.openingWidth, '1.0-0')}`
    );
    const openingDiameter: IHeroCardRow = row($localize`:@@labelOpeningDiameter:Диаметр проёма`, mm(config.openingLength));
    const steps: IHeroCardRow[] = [
        row($localize`:@@homeMiniStairSteps:Ступеней`, number(result.stepCount, '1.0-0')),
        row($localize`:@@homeMiniStairRise:Подъём`, mm(result.riserHeight)),
        row($localize`:@@homeMiniStairTread:Проступь`, mm(result.treadDepth)),
        row($localize`:@@homeMiniStairAngle:Угол`, `${number(result.stairAngle, '1.0-1')}${unitDeg}`),
    ];
    const beforeTurn: IHeroCardRow = row(
        $localize`:@@labelStepsBeforeTurn:Ступеней до поворота`,
        number(result.flight1Count ?? 0, '1.0-0')
    );
    const flightWidth: IHeroCardRow = row($localize`:@@homeMiniStairWidth:Ширина марша`, mm(config.stairWidth));
    const flightRun: IHeroCardRow = row($localize`:@@homeMiniStairRun:Длина марша`, mm(result.totalRun));
    const blondel: IHeroCardRow = checkRow(
        $localize`:@@homeMiniCheckBlondel:Блондель`,
        number(result.blondelValue, '1.0-0'),
        passes(result, 'BLONDEL')
    );
    const diameter: IHeroCardRow = row($localize`:@@labelSpiralDiameter:Диаметр лестницы`, mm(config.spiralDiameter ?? 0));
    const materials: IHeroCardRow[] = [
        row($localize`:@@homeMiniMatTread:Проступь`, woodName(config.treadMaterialId)),
        row($localize`:@@homeMiniMatRiser:Подступёнок`, woodName(config.riserMaterialId)),
        row($localize`:@@homeMiniMatStringer:Тетива`, woodName(config.stringerMaterialId)),
    ];
    const railing: IHeroCardRow[] = [
        row($localize`:@@labelRailingHeight:Высота ограждения`, mm(config.railing.height)),
        row($localize`:@@labelHandrailWidth:Ширина поручня`, mm(config.railing.handrailWidth)),
        row($localize`:@@labelBalusterSpacing:Шаг балясин`, mm(config.railing.balusterSpacing)),
    ];
    const cost: IHeroCardRow[] = [
        row($localize`:@@homeMiniCostVolume:Древесина`, `${number(result.woodVolumeM3, '1.3-3')} ${unitM3}`),
        row($localize`:@@homeMiniCostWeight:Вес`, `${number(result.estimatedWeightKg, '1.0-0')} ${unitKg}`),
        ...costRows(`$${number(result.estimatedCostUsd, '1.0-0')}`),
    ];
    const headroom: IHeroCardRow = checkRow($localize`:@@homeMiniCheckHeadroom:Просвет`, mm(result.headroom), passes(result, 'HEADROOM'));

    const spiral: boolean = variant === EHeroVariant.C || variant === EHeroVariant.D;
    const cta: IHeroCardCta = spiral
        ? { label: $localize`:@@homeHeroCtaSpiral:Открыть конструктор винтовой →`, link: '/calc/spiral' }
        : { label: $localize`:@@homeHeroCtaL:Открыть конструктор Г-образной →`, link: '/calc/l' };

    const roomWord: string = $localize`:@@homeMiniGroupRoom:Помещение`;
    const roomText: string = $localize`:@@homeHeroCardRoomText:Длина, ширина, высота, толщина перекрытия, положение проёма.`;
    const roomTitle: string = $localize`:@@homeHeroCardRoomTitle:Всё начинается с помещения.`;
    const roomPlanTitle: string = $localize`:@@homeHeroCardRoomPlanTitle:Нарисуйте помещение в плане.`;
    const geometryWord: string = $localize`:@@homeMiniGroupGeometry:Геометрия`;
    const geometryText: string = $localize`:@@homeHeroCardGeometryText:Количество ступеней, высота подъёма, ширина проступи, угол наклона.`;
    const materialsWord: string = $localize`:@@homeMiniGroupMaterial:Материалы`;
    const materialsText: string = $localize`:@@homeHeroCardMaterialsText:Порода дерева, сечение тетивы, толщина проступи, перила и балясины.`;
    const woodHouse: string = $localize`:@@homeHeroCardWoodHouseTitle:Сначала дерево, потом дом.`;

    const quote: IHeroCardDraft = {
        ...card('04', $localize`:@@homeMiniGroupCost:Объём и расчёт`, $localize`:@@homeHeroCardQuoteTitle:От размеров`, '', [
            ...cost,
            headroom,
        ]),
        cta,
        titleEm: $localize`:@@homeHeroCardQuoteTitleEm:к расчёту.`,
    };
    const exportCard: IHeroCardDraft = {
        ...card(
            '04',
            $localize`:@@homeSpecExportTitle:Экспорт`,
            $localize`:@@homeHeroCardExportTitle:JSON, PNG и DXF.`,
            $localize`:@@homeHeroCardExportText:Конфигурация сохраняется в URL.`,
            cost
        ),
        cta,
    };

    switch (variant) {
        case EHeroVariant.A:
            return [
                card('01', roomWord, roomTitle, roomText, [...roomSize, slab, opening]),
                card('02', geometryWord, $localize`:@@homeHeroCardBuildsTitle:Лестница собирается сама.`, geometryText, [
                    ...steps,
                    beforeTurn,
                    flightWidth,
                    flightRun,
                    blondel,
                ]),
                card('03', materialsWord, $localize`:@@homeHeroCardMaterialsTitle:Каждая деталь — своей породы.`, materialsText, materials),
                quote,
            ];
        case EHeroVariant.B:
            return [
                card('01', roomWord, roomPlanTitle, roomText, [...roomSize, slab]),
                card('02', geometryWord, $localize`:@@homeHeroCardStepsPlanTitle:Ступени вырастают из плана.`, geometryText, [
                    ...steps,
                    beforeTurn,
                    flightRun,
                    blondel,
                ]),
                card('03', materialsWord, woodHouse, materialsText, materials),
                exportCard,
            ];
        case EHeroVariant.C:
            return [
                card('01', roomWord, roomTitle, roomText, [...roomSize, slab, openingDiameter]),
                card(
                    '02',
                    $localize`:@@homeTypeTitleSpiral:Винтовая`,
                    $localize`:@@homeHeroCardSpiralTitle:Вокруг центральной стойки.`,
                    $localize`:@@homeHeroCardSpiralText:Минимальная площадь в плане. Сложнее в изготовлении.`,
                    [diameter, ...steps, blondel]
                ),
                card(
                    '03',
                    $localize`:@@sectionRailing:Ограждения`,
                    $localize`:@@homeHeroCardRailingsTitle:Сквозь перекрытие, а не рядом.`,
                    $localize`:@@homeHeroCardRailingsText:Высота ограждения, ширина поручня, ширина и шаг балясин.`,
                    railing
                ),
                quote,
            ];
        case EHeroVariant.D:
            return [
                card('01', roomWord, roomPlanTitle, roomText, [...roomSize, openingDiameter]),
                card('02', geometryWord, $localize`:@@homeHeroCardSpiralPlanTitle:Винтовая вырастает из плана.`, geometryText, [
                    diameter,
                    ...steps,
                    blondel,
                ]),
                card(
                    '03',
                    materialsWord,
                    woodHouse,
                    $localize`:@@homeHeroCardWoodListText:Дуб, ясень, бук, берёза, сосна, лиственница — с плотностью каждой породы.`,
                    []
                ),
                exportCard,
            ];
    }
}

/**
 * The four cards of a variant. A and B show the L-shaped stair, C and D the spiral one; `calculation`
 * is the calculator's answer for that type, `locale` formats the numbers
 */
export function heroCards(variant: EHeroVariant, calculation: IHeroCalculation, locale: string): IHeroCard[] {
    const drafts: IHeroCardDraft[] = heroCardDrafts(variant, calculation, locale);
    return HERO_VARIANTS[variant].stages.map(([from, to]: [number, number], index: number): IHeroCard => ({ ...drafts[index], from, to }));
}
